/** @format */

import { createRouter, createWebHistory } from 'vue-router'
import Login from '../views/Auth/Login.vue'
import InternalLogin from '../views/Auth/InternalLogin.vue'
import ResetPasswordRequest from '../views/Auth/ResetPasswordRequest.vue'
import SetNewPassword from '../views/Auth/SetNewPassword.vue'
import Register from '../views/Auth/Register.vue'
import InternalRegister from '../views/Auth/InternalRegister.vue'
import InvalidInvite from '../views/Auth/InvalidInvite.vue'
import ExpiredInvite from '../views/Auth/ExpiredInvite.vue'
import { useAuthStore } from '../stores/auth'
import { useUserStore } from '../stores/user'
import { useModalStore } from '../stores/modal'
import { useMissingTranslationsStore } from '../stores/missingTranslations'
import { resetSeenMissingKeys } from '../config/vueI18n'
import { useCafStore } from '../stores/caf'
import { LINXFOUR_MASTER_USER_ID } from '@/data/constants'

// Auth check function
function isAuthenticated() {
	const authStore = useAuthStore()
	return authStore.isAuthenticated
}

// Routes a partner user is permitted to reach; everything else redirects to the partner index.
export function isPartnerAllowedPath(path) {
	return (
		path.startsWith('/app/partner-contract') ||
		path.startsWith('/app/user/profile') ||
		path.startsWith('/app/user/change-password')
	)
}

export function partnerLandingFor(path) {
	const match = path.match(/^\/app\/contract\/(show|files)\/(\d+)\/?$/)
	if (!match) return { name: 'PartnerContractIndex' }

	return {
		name: match[1] === 'show' ? 'PartnerContractShow' : 'PartnerContractFiles',
		params: { id: match[2] },
	}
}

async function waitForUserData() {
	const userStore = useUserStore()

	if (userStore.userData.id) {
		return
	}

	await new Promise((resolve) => {
		const unsubscribe = userStore.$subscribe((mutation, state) => {
			if (state.userData.id) {
				unsubscribe()
				resolve()
			}
		})
	})
}

const requiresOem = async (to, from, next) => {
	const authStore = useAuthStore()
	const userStore = useUserStore()
	const modalStore = useModalStore()

	await waitForUserData()

	const isInternalUser = authStore.currentUserType === 'internal'
	const isOwnedByLinxfour =
		Number(LINXFOUR_MASTER_USER_ID) === Number(userStore.userData.owned_by)

	if (isInternalUser && isOwnedByLinxfour) {
		if (userStore.can('login as oem')) {
			next()

			modalStore.setOpen({
				name: 'ActAsUserModal',
				title: '§ui.modal_title_select_master',
				iconName: 'check',
			})
			return
		}

		modalStore.setOpen({
			name: 'ConfirmActionModal',
			size: 'small',
			title: '§ui.modal_title_internal_user_no_oem_login',
			data: {
				cancelButtonLabel: '§ui_button.cancel',
			},
		})
		next(from)
		return
	}

	next()
}

const requiresPermission = (permission, fallbackRouteName) => async (to, from, next) => {
	const userStore = useUserStore()

	await waitForUserData()

	if (userStore.can(permission)) {
		next()
	} else {
		next({ name: fallbackRouteName })
	}
}

export const requiresInternalUser = (fallbackRouteName) => async (to, from, next) => {
	await waitForUserData()

	if (useUserStore().isInternalUser) {
		next()
	} else {
		next({ name: fallbackRouteName, params: to.params })
	}
}

const router = createRouter({
	history: createWebHistory(),
	routes: [
		{
			path: '/password/reset/:token',
			name: 'reset_password',
			component: SetNewPassword,
			meta: {
				requiresAuth: false,
				guestOnly: true,
			},
			props: true,
		},

		{
			path: '/login',
			name: 'login',
			component: Login,
			meta: {
				requiresAuth: false,
				guestOnly: true,
			},
		},
		{
			path: '/internal',
			name: 'InternalLogin',
			component: InternalLogin,
			meta: {
				requiresAuth: false,
				guestOnly: true,
			},
		},

		{
			path: '/password/reset_request/',
			name: 'reset_password_request',
			component: ResetPasswordRequest,
			meta: {
				requiresAuth: false,
				guestOnly: true,
			},
		},

		{
			path: '/invited/expired',
			name: 'ExpiredInvite',
			component: ExpiredInvite,
			meta: {
				requiresAuth: false,
				guestOnly: true,
			},
			props: true,
		},
		{
			path: '/invited/invalid',
			name: 'InvalidInvite',
			component: InvalidInvite,
			meta: {
				requiresAuth: false,
				guestOnly: true,
			},
			props: true,
		},
		{
			path: '/invited/:invite',
			name: 'invited',
			component: Register,
			meta: {
				requiresAuth: false,
				guestOnly: true,
			},
			props: true,
		},
		{
			path: '/internal-invited/:internal_invite',
			name: 'internalInvited',
			component: InternalRegister,
			meta: {
				requiresAuth: false,
				guestOnly: true,
			},
			props: true,
		},

		{
			path: '/',
			redirect: '/app/home',
		},

		// App routes - all protected
		{
			path: '/app',
			meta: {
				requiresAuth: true,
			},
			children: [
				{
					path: '',
					redirect: { name: 'home' },
				},

				{
					path: 'home',
					name: 'home',
					component: () =>
						import(
							/* webpackChunkName: "user-views-wrapper" */ '@/views/Home/Home.vue'
						),
					props: true,
					meta: {
						requiresAuth: true,
					},
				},

				{
					path: 'click-and-finance',
					name: 'ClickAndFinance',
					component: () =>
						import(
							/* webpackChunkName: "click-and-finance" */ '@/views/ClickAndFinance/ClickAndFinance.vue'
						),
					props: true,
					meta: { requiresAuth: true },
					beforeEnter: (to, from, next) => requiresOem(to, from, next),

					children: [
						{
							path: 'form',
							name: 'ClickAndFinanceForm',
							component: () =>
								import(
									/* webpackChunkName: "click-and-finance-form" */ '@/views/ClickAndFinance/ClickAndFinanceForm.vue'
								),
							props: true,
						},
						{
							path: 'calculation',
							name: 'ClickAndFinanceCalculation',
							component: () =>
								import(
									/* webpackChunkName: "click-and-finance-calculation" */ '@/views/ClickAndFinance/ClickAndFinanceCalculation.vue'
								),
							props: true,
							beforeEnter: (to, from, next) => {
								const cafStore = useCafStore()
								if (cafStore.formStatus !== 'calculated') {
									next({ name: 'ClickAndFinanceForm' })
								} else {
									next()
								}
							},
						},
					],
				},

				{
					path: 'admin',
					name: 'AdminViewsWrapper',
					component: () =>
						import(
							/* webpackChunkName: "admin-views-wrapper" */ '@/views/Admin/AdminViewsWrapper.vue'
						),
					props: true,
					meta: { requiresAuth: true },

					children: [
						{
							path: 'caf-settings',
							name: 'AdminCafSettings',
							component: () =>
								import(
									/* webpackChunkName: "admin-caf-settings" */ '@/views/Admin/AdminCafSettings.vue'
								),
							props: true,
						},
						{
							path: 'contract-approval',
							name: 'AdminContractApprovalSettings',
							component: () =>
								import(
									/* webpackChunkName: "admin-contract-approval-settings" */ '@/views/Admin/AdminContractApprovalSettings.vue'
								),
							props: true,
						},
						{
							path: 'credit-risk',
							name: 'AdminCreditRiskSettings',
							component: () =>
								import(
									/* webpackChunkName: "admin-credit-risk-settings" */ '@/views/Admin/AdminCreditRiskSettings.vue'
								),
							props: true,
						},
						{
							path: 'country-index',
							name: 'AdminCountryIndex',
							component: () =>
								import(
									/* webpackChunkName: "admin-country-index" */ '@/views/Admin/AdminCountryIndex.vue'
								),
							props: true,
						},
						{
							path: 'invoice-settings',
							name: 'AdminInvoiceSettings',
							component: () =>
								import(
									/* webpackChunkName: "admin-invoice-settings" */ '@/views/Admin/AdminInvoiceSettings.vue'
								),
							props: true,
						},
						{
							path: 'calculator-settings',
							name: 'AdminCalculatorSettings',
							component: () =>
								import(
									/* webpackChunkName: "admin-calculator-settings" */ '@/views/Admin/AdminCalculatorSettings.vue'
								),
							props: true,
						},
						{
							path: 'billing-unit-index',
							name: 'BillingUnitIndex',
							component: () =>
								import(
									/* webpackChunkName: "billing-unit-index" */ '@/views/Admin/BillingUnitIndex.vue'
								),
							props: true,
						},
						{
							path: 'billing-entity-index',
							name: 'BillingEntityIndex',
							component: () =>
								import(
									/* webpackChunkName: "billing-entity-index" */ '@/views/Admin/BillingEntityIndex.vue'
								),
							props: true,
						},
						{
							path: 'emails-compliance-settings',
							name: 'AdminEmailsComplianceSettings',
							component: () =>
								import(
									/* webpackChunkName: "admin-emails-compliance-settings" */ '@/views/Admin/AdminEmailsComplianceSettings.vue'
								),
							props: true,
						},
						{
							path: 'role-management',
							name: 'AdminRoleManagement',
							component: () =>
								import(
									/* webpackChunkName: "admin-role-management" */ '@/views/Admin/AdminRoleManagement.vue'
								),
							props: true,
						},
						{
							path: 'swap-rates',
							name: 'AdminSwapRates',
							component: () =>
								import(
									/* webpackChunkName: "admin-swap-rates" */ '@/views/Admin/AdminSwapRates.vue'
								),
							props: true,
						},
					],
				},

				{
					path: 'client',
					name: 'ClientViewsWrapper',
					component: () =>
						import(
							/* webpackChunkName: "client-views-wrapper" */ '@/views/Client/ClientViewsWrapper.vue'
						),
					props: true,
					meta: { requiresAuth: true },

					children: [
						{
							path: 'index-internal',
							name: 'ClientIndexInternal',
							component: () =>
								import(
									/* webpackChunkName: "client-index-internal" */ '@/views/Client/ClientIndexInternal.vue'
								),
							props: true,
						},

						{
							path: 'index',
							name: 'ClientIndex',
							component: () =>
								import(
									/* webpackChunkName: "client-index" */ '@/views/Client/ClientIndex.vue'
								),
							props: true,
						},

						{
							path: 'create',
							name: 'ClientCreate',
							component: () =>
								import(
									/* webpackChunkName: "client-create" */ '@/views/Client/ClientItem.vue'
								),
							props: true,
							beforeEnter: (to, from, next) => requiresOem(to, from, next),
						},

						{
							path: 'show/:id',
							name: 'ClientShow',
							component: () =>
								import(
									/* webpackChunkName: "client-show" */ '@/views/Client/ClientItem.vue'
								),
							props: true,
							meta: { clientItem: true },
						},

						{
							path: 'files/:id',
							name: 'ClientFiles',
							component: () =>
								import(
									/* webpackChunkName: "client-files" */ '@/views/Client/ClientFiles.vue'
								),
							props: true,
							meta: { clientItem: true },
							beforeEnter: requiresInternalUser('ClientShow'),
						},

						{
							path: 'kyc/:id',
							name: 'ClientKyc',
							component: () =>
								import(
									/* webpackChunkName: "client-kyc" */ '@/views/Client/ClientKyc.vue'
								),
							props: true,
							meta: { clientItem: true },
						},

						{
							path: 'kyc/:id/report/:reportId',
							name: 'ComplianceReportView',
							component: () =>
								import(
									/* webpackChunkName: "compliance-report-view" */ '@/views/Client/ComplianceReportView.vue'
								),
							props: true,
							meta: { clientItem: true },
						},
					],
				},

				{
					path: 'contract',
					name: 'ContractViewsWrapper',
					component: () =>
						import(
							/* webpackChunkName: "contract-views-wrapper" */ '@/views/Contract/ContractViewsWrapper.vue'
						),
					props: true,
					meta: { requiresAuth: true },

					children: [
						{
							path: 'index',
							name: 'ContractIndex',
							component: () =>
								import(
									/* webpackChunkName: "contract-index" */ '@/views/Contract/ContractIndex.vue'
								),
							props: true,
						},
						{
							path: 'show/:id',
							name: 'ContractShow',
							component: () =>
								import(
									/* webpackChunkName: "contract-show" */ '@/views/Contract/ContractShow.vue'
								),
							props: true,
						},
						{
							path: 'update/:id',
							name: 'ContractUpdate',
							component: () =>
								import(
									/* webpackChunkName: "contract-update" */ '@/views/Contract/ContractUpdate.vue'
								),
							props: true,
						},
						{
							path: 'invoices/:id',
							name: 'ContractInvoices',
							component: () =>
								import(
									/* webpackChunkName: "contract-invoices" */ '@/views/Contract/ContractInvoices.vue'
								),
							props: true,
						},
						{
							path: 'files/:id',
							name: 'ContractFiles',
							component: () =>
								import(
									/* webpackChunkName: "contract-files" */ '@/views/Contract/ContractFiles.vue'
								),
							props: true,
						},
						{
							path: 'history/:id',
							name: 'ContractHistory',
							component: () =>
								import(
									/* webpackChunkName: "contract-history" */ '@/views/Contract/ContractHistory.vue'
								),
							props: true,
						},
					],
				},

				{
					path: 'partner-contract',
					name: 'PartnerContractViewsWrapper',
					component: () =>
						import(
							/* webpackChunkName: "partner-contract-views-wrapper" */ '@/views/PartnerContract/PartnerContractViewsWrapper.vue'
						),
					props: true,
					meta: { requiresAuth: true },

					children: [
						{
							path: 'index',
							name: 'PartnerContractIndex',
							component: () =>
								import(
									/* webpackChunkName: "partner-contract-index" */ '@/views/PartnerContract/PartnerContractIndex.vue'
								),
							props: true,
						},
						{
							path: 'show/:id',
							name: 'PartnerContractShow',
							component: () =>
								import(
									/* webpackChunkName: "partner-contract-show" */ '@/views/PartnerContract/PartnerContractShow.vue'
								),
							props: true,
						},
						{
							path: 'files/:id',
							name: 'PartnerContractFiles',
							component: () =>
								import(
									/* webpackChunkName: "partner-contract-files" */ '@/views/PartnerContract/PartnerContractFiles.vue'
								),
							props: true,
						},
						{
							path: 'tradefiles',
							name: 'PartnerTradefiles',
							component: () =>
								import(
									/* webpackChunkName: "partner-tradefiles" */ '@/views/PartnerContract/PartnerTradefiles.vue'
								),
							props: true,
							beforeEnter: requiresPermission(
								'view us bank tradefiles',
								'PartnerContractIndex'
							),
						},
					],
				},

				{
					path: 'invoices',
					name: 'InvoicesViewsWrapper',
					component: () =>
						import(
							/* webpackChunkName: "operations-management-views-wrapper" */ '@/views/OperationsManagement/OperationsManagementViewsWrapper.vue'
						),
					props: true,
					meta: { requiresAuth: true },

					children: [
						{
							path: 'index',
							name: 'InvoicesIndex',
							component: () =>
								import(
									/* webpackChunkName: "operations-management-invoice-index" */ '@/views/OperationsManagement/InvoicesIndex.vue'
								),
							props: true,
						},
						{
							path: 'show/:id',
							name: 'InvoicesShow',
							component: () =>
								import(
									/* webpackChunkName: "operations-management-invoice-show" */ '@/views/OperationsManagement/InvoicesShow.vue'
								),
							props: true,
						},
					],
				},

				{
					path: 'operations-management',
					name: 'OperationsManagementViewsWrapper',
					component: () =>
						import(
							/* webpackChunkName: "operations-management-views-wrapper" */ '@/views/OperationsManagement/OperationsManagementViewsWrapper.vue'
						),
					props: true,
					meta: { requiresAuth: true },

					children: [
						{
							path: 'missing-contract-documents-overview',
							name: 'MissingContractDocumentsOverview',
							component: () =>
								import(
									/* webpackChunkName: "missing-contract-documents-overview" */ '@/views/OperationsManagement/MissingContractDocumentsOverview.vue'
								),
							props: true,
						},
						{
							path: 'annual-checks',
							name: 'AnnualChecks',
							component: () =>
								import(
									/* webpackChunkName: "annual-checks" */ '@/views/OperationsManagement/AnnualChecks.vue'
								),
							props: true,
						},
						{
							path: 'dnb-metrics',
							name: 'DnbMetrics',
							component: () =>
								import(
									/* webpackChunkName: "dnb-metrics" */ '@/views/OperationsManagement/DnbMetrics.vue'
								),
							props: true,
						},
					],
				},

				{
					path: 'token',
					name: 'TokenViewsWrapper',
					component: () =>
						import(
							/* webpackChunkName: "token-views-wrapper" */ '@/views/Token/TokenViewsWrapper.vue'
						),
					props: true,
					meta: { requiresAuth: true },

					children: [
						{
							path: 'index',
							name: 'TokenIndex',
							component: () =>
								import(
									/* webpackChunkName: "token-index" */ '@/views/Token/TokenIndex.vue'
								),
							props: true,
						},
					],
				},

				{
					path: 'unit',
					name: 'UnitViewsWrapper',
					component: () =>
						import(
							/* webpackChunkName: "unit-views-wrapper" */ '@/views/Unit/UnitViewsWrapper.vue'
						),
					props: true,
					meta: { requiresAuth: true },

					children: [
						{
							path: 'index',
							name: 'UnitIndex',
							component: () =>
								import(
									/* webpackChunkName: "unit-index" */ '@/views/Unit/UnitIndex.vue'
								),
							props: true,
						},
						{
							path: 'index-inactive',
							name: 'UnitIndexInactive',
							component: () =>
								import(
									/* webpackChunkName: "unit-index" */ '@/views/Unit/UnitIndex.vue'
								),
							props: true,
						},
						{
							path: 'show/:id',
							name: 'UnitShow',
							component: () =>
								import(
									/* webpackChunkName: "unit-show" */ '@/views/Unit/UnitShow.vue'
								),
							props: true,
						},
						{
							path: 'utilization/:id',
							name: 'UnitUtilization',
							component: () =>
								import(
									/* webpackChunkName: "unit-utilization" */ '@/views/Unit/UnitUtilization.vue'
								),
							props: true,
						},
					],
				},

				{
					path: 'unit-type',
					name: 'UnitTypeViewsWrapper',
					component: () =>
						import(
							/* webpackChunkName: "unit-type-views-wrapper" */ '@/views/UnitType/UnitTypeViewsWrapper.vue'
						),
					props: true,
					meta: { requiresAuth: true },

					children: [
						{
							path: 'index',
							name: 'UnitTypeIndex',
							component: () =>
								import(
									/* webpackChunkName: "unit-type-index" */ '@/views/UnitType/UnitTypeIndex.vue'
								),
							props: true,
						},
						{
							path: 'create',
							name: 'UnitTypeCreate',
							component: () =>
								import(
									/* webpackChunkName: "unit-type-create" */ '@/views/UnitType/UnitTypeCreate.vue'
								),
							props: true,
							meta: { requiresAuth: true },
							beforeEnter: (to, from, next) => requiresOem(to, from, next),
						},
						{
							path: 'show/:id',
							name: 'UnitTypeShow',
							component: () =>
								import(
									/* webpackChunkName: "unit-type-show" */ '@/views/UnitType/UnitTypeShow.vue'
								),
							props: true,
						},
					],
				},

				{
					path: 'user',
					name: 'UserViewsWrapper',
					component: () =>
						import(
							/* webpackChunkName: "user-views-wrapper" */ '@/views/User/UserViewsWrapper.vue'
						),
					props: true,
					meta: { requiresAuth: true },

					children: [
						{
							path: 'profile',
							name: 'UserProfile',
							component: () =>
								import(
									/* webpackChunkName: "user-profile" */ '@/views/User/UserProfile.vue'
								),
							props: true,
						},
						{
							path: 'change-password',
							name: 'UserChangePassword',
							component: () =>
								import(
									/* webpackChunkName: "user-change-password" */ '@/views/User/UserChangePassword.vue'
								),
							props: true,
						},
						{
							path: 'digital-id',
							name: 'UserDigitalId',
							component: () =>
								import(
									/* webpackChunkName: "user-digital-id" */ '@/views/User/UserDigitalId.vue'
								),
							props: true,
						},
					],
				},

				{
					path: 'users',
					name: 'UsersViewsWrapper',
					component: () =>
						import(
							/* webpackChunkName: "users-views-wrapper" */ '@/views/Users/UsersViewsWrapper.vue'
						),
					props: true,
					meta: { requiresAuth: true },

					children: [
						{
							path: 'index',
							name: 'UsersIndex',
							component: () =>
								import(
									/* webpackChunkName: "users-index" */ '@/views/Users/UsersIndex.vue'
								),
							props: true,
						},
						{
							path: 'internal-users',
							name: 'InternalUsersIndex',
							component: () =>
								import(
									/* webpackChunkName: "users-index" */ '@/views/Users/InternalUsersIndex.vue'
								),
							props: true,
						},
						{
							path: 'invite',
							name: 'UsersInvite',
							component: () =>
								import(
									/* webpackChunkName: "users-invite" */ '@/views/Users/UsersInvite.vue'
								),
							props: true,
							beforeEnter: (to, from, next) => requiresOem(to, from, next),
						},
						{
							path: 'invite-external-partner',
							name: 'ExternalPartnerInvite',
							component: () =>
								import(
									/* webpackChunkName: "external-partner-invite" */ '@/views/Users/ExternalPartnerInvite.vue'
								),
							props: true,
						},
						{
							path: 'invite-internal',
							name: 'InternalUsersInvite',
							component: () =>
								import(
									/* webpackChunkName: "users-invite-internal" */ '@/views/Users/InternalUsersInvite.vue'
								),
							props: true,
						},
						{
							path: 'create-oem',
							name: 'CreateOem',
							component: () =>
								import(
									/* webpackChunkName: "create-oem" */ '@/views/Users/CreateOem.vue'
								),
							props: true,
						},
					],
				},

				{
					path: 'not-found',
					name: 'PageNotFound',
					component: () =>
						import(
							/* webpackChunkName: "page-not-found" */ '@/views/Home/PageNotFound.vue'
						),
					props: true,
					meta: { requiresAuth: true },
				},
			],
		},

		// Catch-all route for 404s
		{
			// path: '/:pathMatch(.*)*',
			path: '/app/:pathMatch(.*)*',
			redirect: '/app/not-found',
		},
	],
})

router.afterEach((to) => {
	resetSeenMissingKeys(to.name)
	useMissingTranslationsStore().clearForRoute()
})

// Navigation Guards
router.beforeEach((to, from, next) => {
	const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
	const guestOnly = to.matched.some((record) => record.meta.guestOnly)

	const authenticated = isAuthenticated()
	const userStore = useUserStore()

	if (requiresAuth && !authenticated) {
		pushToLogin(to, from, next)
	} else if (guestOnly && authenticated) {
		next(userStore.isPartner ? { name: 'PartnerContractIndex' } : { name: 'home' })
	} else if (
		authenticated &&
		userStore.isPartner &&
		requiresAuth &&
		!isPartnerAllowedPath(to.path)
	) {
		next(partnerLandingFor(to.path))
	} else {
		next()
	}
})

const pushToLogin = (to, from, next) => {
	const userStore = useUserStore()
	const authStore = useAuthStore()
	const isFromInternal = from.name === 'InternalLogin' || authStore.currentUserType === 'internal'
	const loginRoute = userStore.isInternalUser || isFromInternal ? 'InternalLogin' : 'login'

	next({ name: loginRoute, query: { redirect: to.fullPath } })
}

export default router
